import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';

import { useContactsContext } from '../contexts/ContactsContext';
import large_logo from '../core/assets/sunshine_large.png';
import { submitContactInfo } from '../api/ContactInfoApi';
import { DeviceQueries } from '../components/styled';
import RequestForm from '../components/forms/RequestForm';

import { TAppAddress, TAppContact, TAppEmail, TAppPhone } from 'TProtocol/common/models';

import appIconPng from '../core/assets/thanks/app_icon.png';
import appIconWebp from '../core/assets/thanks/app_icon.webp';
import appIconMask from '../core/assets/thanks/app_icon_mask.svg';
import appStore from '../core/assets/thanks/app_store.svg';
import card1Jpg from '../core/assets/thanks/card1.jpeg';
import card1Webp from '../core/assets/thanks/card1.webp';
import card2Jpg from '../core/assets/thanks/card2.jpeg';
import card2Webp from '../core/assets/thanks/card2.webp';
import card3Jpg from '../core/assets/thanks/card3.jpeg';
import card3Webp from '../core/assets/thanks/card3.webp';
import card4Jpg from '../core/assets/thanks/card4.jpeg';
import card4Webp from '../core/assets/thanks/card4.webp';
import card5Jpg from '../core/assets/thanks/card5.jpeg';
import card5Webp from '../core/assets/thanks/card5.webp';

interface IUContactInfoParameters {
  fullName: string;
  token: string;
}

const EngagementPage = () => {
  const contactsContext = useContactsContext();
  const [searchParams] = useSearchParams();
  const [requesterName, setRequesterName] = useState('A Sunshine Smart Contacts user');
  const [token, setToken] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [savingError, setSavingError] = useState<string | null>(null);

  useEffect(() => {
    contactsContext.setContactToEdit(new TAppContact({
      otherPhones: [
        new TAppPhone({})
      ],
      otherEmails: [
        new TAppEmail({})
      ],
      otherAddresses: [
        new TAppAddress({})
      ]
    }));
    try {
      const paramsStr = searchParams.get('params');
      if (paramsStr !== null) {
        const params = JSON.parse(window.atob(paramsStr)) as IUContactInfoParameters;
        setRequesterName(params.fullName);
        setToken(params.token);
      } else {
        setLoadingError('Sorry, this request is not valid.');
      }
    } catch (e) {
      setLoadingError('Sorry, this request is not valid.');
    }
  }, []);

  const onSubmit = async (contact: TAppContact) => {
    try {
      await submitContactInfo({ token, contact });
      setSubmitted(true);
    } catch (e) {
      setSavingError(`${e.message ?? e}`);
    }
  };

  let content;
  if (loadingError) {
    content = <>
      <Error>
        {loadingError}
      </Error>
      Please have the Sunshine user who requested your information try again, or contact our Customer Support at <a
      href="mailto:support@sunshine.com">support@sunshine.com</a> if the problem persists.
    </>;
  } else if (submitted) {
    content = <>
      <h3>Thank you!</h3>
      <ConfirmationMessage>We have sent your updated information to {requesterName}.</ConfirmationMessage>

      <ActionContainer
        onClick={() => window.location.href = 'https://apps.apple.com/us/app/sunshine-smart-contacts/id1581495939'}>
        <AppIconContainer>
          <AppIconMask src={appIconMask}/>
          <AppIcon>
            <source srcSet={appIconWebp} type="image/webp"/>
            <source srcSet={appIconPng} type="image/png"/>
            <img src={appIconPng} alt="Sunshine Smart Contacts"/>
          </AppIcon>
        </AppIconContainer>

        <ThankYouMessage> Would you like more organized, complete contacts? <a
          href="https://apps.apple.com/us/app/sunshine-smart-contacts/id1581495939">Try Sunshine Smart
          Contacts!</a></ThankYouMessage>
      </ActionContainer>

      <AppCardContainer>
        <AppCard>
          <source srcSet={card1Webp} type="image/webp"/>
          <source srcSet={card1Jpg} type="image/jpeg"/>
          <img src={card1Jpg} alt="Make contacts just work."/>
        </AppCard>

        <AppCard>
          <source srcSet={card2Webp} type="image/webp"/>
          <source srcSet={card2Jpg} type="image/jpeg"/>
          <img src={card2Jpg} alt="The world's most advanced and intuitive contact manager."/>
        </AppCard>

        <AppCard>
          <source srcSet={card3Webp} type="image/webp"/>
          <source srcSet={card3Jpg} type="image/jpeg"/>
          <img src={card3Jpg} alt="Organized & Complete"/>
        </AppCard>

        <AppCard>
          <source srcSet={card4Webp} type="image/webp"/>
          <source srcSet={card4Jpg} type="image/jpeg"/>
          <img src={card4Jpg} alt="Share With Ease"/>
        </AppCard>

        <AppCard>
          <source srcSet={card5Webp} type="image/webp"/>
          <source srcSet={card5Jpg} type="image/jpeg"/>
          <img src={card5Jpg} alt="Always Keep In Touch"/>
        </AppCard>
      </AppCardContainer>


      <Container>
        <a href="https://apps.apple.com/us/app/sunshine-smart-contacts/id1581495939">
          <img src={appStore}/>
        </a>
      </Container>
    </>;
  } else {
    content = <>
      <IntroductionText>
        {requesterName} would like your info:
      </IntroductionText>

      {savingError && <ErrorBox>
        <p><strong>
          Sorry, something went wrong submitting your information.
        </strong></p>
        Please try again, or contact our Customer Support at <a
        href="mailto:support@sunshine.com">support@sunshine.com</a> if the problem persists.
      </ErrorBox>}

      <RequestForm onSubmit={onSubmit}/>

    </>;
  }

  return (
    <PageContainer>
      <CenteredContent>
        <Container>
          <Logo src={large_logo}/>
        </Container>
        {content}
      </CenteredContent>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  text-align: center;
`;

const CenteredContent = styled.div`
  display: inline-block;
  margin: auto;
  padding: 0 24px 10px;
  text-align: start;
  width: 600px;

  @media (${DeviceQueries.mobile}) {
    display: block;
    width: 100%;
  }`;

const Container = styled.div`
  width: 100%;
  text-align: center;
`;

const Logo = styled.img`
  max-width: 200px;
  margin: 40px auto;
`;

const IntroductionText = styled.div`
  margin-bottom: 20px;
`;

const Error = styled.div`
  color: #cc3333;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const ErrorBox = styled.div`
  background-color: #dc3545;
  color: #ffffff;
  border-radius: 6px;
  padding: 5px;

  && {
    a {
      color: #ffffff;
    }
  }
`;

const ConfirmationMessage = styled.div`
  margin-bottom: 20px;
`;

const ThankYouMessage = styled.div`
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 30px;
`;

const ActionContainer = styled.div`
  cursor: pointer;
`;

const AppIconContainer = styled.div`
  float: left;
  width: 100px;
  height: 100px;
  margin-right: 10px;
  clear-after: both;
`;

const AppIcon = styled.picture`
  img {
    border-radius: 22.5%;
    width: 100%;
  }
`;

const AppCardContainer = styled.div`
  padding: 20px 0;
  clear: left;
  text-align: center;

  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  @media (${DeviceQueries.mobile}) {
    width: 410px;
  }
`;

const AppCard = styled.picture`
  clear: both;
  margin: 0 5px;

  scroll-snap-align: start;
  flex-shrink: 0;
  transform-origin: center center;
  transform: scale(1);
  transition: transform 0.5s;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 200px;
    border-radius: 10%/4.7%;
  }
`;

const AppIconMask = styled.img`
  position: absolute;
  width: 100px;
`;

export default EngagementPage;
